import React from 'react';
import { OrgChart } from '../components/OrgChart';
import { Users, Target, BookOpen, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const About = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background Image */}
      <section 
        className="relative py-24 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 to-primary-900/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-6">Le Cercle Poincaré</h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Un lieu d'excellence et d'intelligence collective au cœur du Grand Est
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Notre Histoire</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Le Cercle Poincaré, présidé par André Rossinot, accompagné de Malika Dati comme secrétaire générale et de Frédéric Richard en qualité de trésorier, est un espace unique de réflexion, de dialogue et d'influence basé à Nancy, au cœur de la région du Grand Est.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ce cercle d'élite regroupe une centaine de membres issus de domaines variés, tous reconnus pour leur expertise et leur engagement envers des valeurs profondément humanistes.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://zupimages.net/up/24/48/9c7l.jpeg"
                alt="Réunion du Cercle Poincaré"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary-600 text-white p-4 rounded-lg shadow-lg">
                <p className="font-semibold">Fondé en 2019</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://www.sitlor.fr/photos/737/placestanislasdenuit.jpg?2024-05-07T10:18:21Z"
                alt="Place Stanislas de nuit"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold mb-6">L'Héritage de l'École de Nancy</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Inspiré par l'esprit novateur et visionnaire du mouvement de l'École de Nancy, le Cercle Poincaré se veut un prolongement contemporain de cette tradition d'excellence intellectuelle et artistique.
              </p>
              <p className="text-gray-600 leading-relaxed">
                L'École de Nancy, connue pour son rôle précurseur dans l'Art nouveau, incarne également des idéaux de collaboration et d'innovation qui résonnent avec les ambitions du Cercle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Notre Mission</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              L'une des missions principales du Cercle est de valoriser le potentiel exceptionnel de la région du Grand Est, qualifiée de « terre d'accueil et d'intelligence collective ».
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Réseau d'Excellence</h3>
              <p className="text-gray-600">
                Accédez à un réseau exclusif de leaders et d'experts reconnus dans leurs domaines.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Influence Régionale</h3>
              <p className="text-gray-600">
                Participez activement au développement et au rayonnement du Grand Est.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold mb-4">Échanges Enrichissants</h3>
              <p className="text-gray-600">
                Bénéficiez d'opportunités uniques de dialogue et de partage d'expériences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Notre Équipe</h2>
          </div>
          <OrgChart />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Rejoignez le Cercle Poincaré</h2>
          <p className="text-primary-100 max-w-2xl mx-auto mb-8">
            Participez à l'aventure et contribuez au rayonnement de notre région.
          </p>
          <Link
            to="/devenir-membre"
            className="inline-flex items-center px-8 py-3 border-2 border-white rounded-lg text-white hover:bg-white hover:text-primary-900 transition-all duration-300"
          >
            Devenir membre
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};